import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ToolCallPayload } from "../../../api/threads/liveMessages";
import { ThreadContext } from "../page/threadPageContext";
import { readableJsonText } from "../../../shared/formatting/readableJson";
import CopyReference from "../../../ui/content/CopyReference";
import { toolCallProblemDetail } from "./toolCallLines";
import { toolCallLinks } from "./toolCallLinks";
import Chip from "../../../ui/feedback/Chip";
import { chipClassName } from "../../../ui/styles/chipStyles";
import { classNames } from "../../../ui/styles/classNames";
import SourceView from "../../../ui/content/source/SourceView";

/** Exact recorded input and output, shared by both call views (web-app.md WA-4). */
export default function ToolCallDetails({ payload, threadId }: { payload: ToolCallPayload; threadId?: string }) {
  const { t } = useTranslation();
  // The Run on screen, where there is one: a fork shows its parent's calls (F164's cues tell their entries apart).
  const shownThreadId = useContext(ThreadContext)?.threadId;
  const detail = toolCallProblemDetail(payload);
  return (
    <div className="mt-2 space-y-2">
      {payload.outcome === "ok" || detail === null ? null : <p role="alert" className="app-status-text-error text-xs">{detail}</p>}
      <p className="app-text-muted text-xs">{t("threads.toolCall.input")}</p>
      <SourceView label={t("threads.toolCall.input")} language="json" value={readableJsonText(payload.arguments, { sortKeys: false })} />
      <p className="app-text-muted text-xs">{t("threads.toolCall.output")}</p>
      <SourceView label={t("threads.toolCall.output")} language="json" value={readableJsonText(payload.result_summary, { sortKeys: false })} />
      <div className="flex flex-wrap gap-1">
        {payload.references_read.map((reference) => (
          <Chip key={reference} className="break-all">{reference}<CopyReference value={reference} /></Chip>
        ))}
        {toolCallLinks(payload, threadId, shownThreadId).map((link) => (
          <Link key={link.href + link.label} to={link.href} className={classNames(chipClassName(), "app-link", link.inThisRun ? "app-link-cue-up" : "app-link-cue-away")}>{link.label}</Link>
        ))}
      </div>
    </div>
  );
}
